import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  Globe,
  Wifi,
  Smartphone,
  Zap,
  Droplets,
  Receipt,
  ChevronRight,
  Plus,
  Check,
  Heart,
  DollarSign,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";
import { v4 as uuidv4 } from "uuid";

// Provider config with logos
const providerConfig = {
  "jps_diaspora": { logo: "/jps-logo.png", name: "JPS", bgColor: "bg-white", borderColor: "border-yellow-500/30" },
  "nwc_diaspora": { logo: "/nwc-logo.png", name: "NWC", bgColor: "bg-white", borderColor: "border-cyan-500/30" },
  "flow_diaspora": { logo: "/flow-logo.png", name: "Flow", bgColor: "bg-white", borderColor: "border-blue-500/30" },
  "digicel_diaspora": { logo: "/digicel-logo.png", name: "Digicel", bgColor: "bg-white", borderColor: "border-red-500/30" },
};

// Provider icons mapping (fallback)
const providerIcons = {
  "jps_diaspora": { icon: Zap, color: "bg-yellow-500", gradient: "from-yellow-500 to-yellow-700" },
  "nwc_diaspora": { icon: Droplets, color: "bg-cyan-500", gradient: "from-cyan-500 to-cyan-700" },
  "flow_diaspora": { icon: Wifi, color: "bg-blue-500", gradient: "from-blue-500 to-blue-700" },
  "digicel_diaspora": { icon: Smartphone, color: "bg-red-500", gradient: "from-red-500 to-red-700" },
};

const relationships = [
  { value: "parent", label: "Parent", icon: "👨‍👩‍👧" },
  { value: "child", label: "Child", icon: "👶" },
  { value: "sibling", label: "Sibling", icon: "👫" },
  { value: "friend", label: "Friend", icon: "🤝" },
  { value: "other", label: "Other", icon: "👤" }
];

const DiasporaBills = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useState("list"); // "list", "add", "pay"
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [nickname, setNickname] = useState("");
  const [selectedBiller, setSelectedBiller] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [relationship, setRelationship] = useState("parent");
  const [paymentStep, setPaymentStep] = useState(1);
  const [paymentResult, setPaymentResult] = useState(null);

  // Fetch diaspora providers
  const { data: providers } = useQuery({
    queryKey: ["diasporaProviders"],
    queryFn: async () => {
      const response = await axios.get(`${API}/bills/diaspora-providers`);
      return response.data;
    }
  });

  // Fetch user's diaspora billers
  const { data: myBillers, isLoading } = useQuery({
    queryKey: ["myBillers"],
    queryFn: async () => {
      const response = await axios.get(`${API}/bills/my-billers`);
      return response.data;
    }
  });

  // Filter to only diaspora billers
  const diasporaBillers = myBillers?.filter(b => b.is_diaspora) || [];

  // Fetch USD wallet
  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  const usdWallet = wallets?.find(w => w.currency === "USD");

  // Add diaspora biller mutation
  const addBillerMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/bills/diaspora/add-biller`, {
        provider_id: selectedProvider.provider_id,
        account_number: accountNumber,
        nickname: nickname || null
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["myBillers"]);
      toast.success("Biller added successfully");
      setView("list");
      setSelectedProvider(null);
      setAccountNumber("");
      setNickname("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to add biller");
    }
  });

  // Pay diaspora bill mutation
  const payMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/bills/diaspora/pay`, {
        biller_id: selectedBiller.biller_id,
        amount_usd: parseFloat(paymentAmount),
        wallet_id: usdWallet.wallet_id,
        recipient_name: recipientName,
        relationship: relationship,
        idempotency_key: uuidv4()
      });
      return response.data;
    },
    onSuccess: (data) => {
      setPaymentResult(data);
      setPaymentStep(3);
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["myBillers"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Payment failed");
    }
  });

  const formatUSD = (amount) => `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  const formatJMD = (amount) => `J$${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  const handleSelectBiller = (biller) => {
    setSelectedBiller(biller);
    // Convert JMD balance to approximate USD (using rough rate)
    const approxUSD = (biller.outstanding_balance / 157).toFixed(2);
    setPaymentAmount(approxUSD);
    setPaymentStep(1);
    setView("pay");
  };

  const handleConfirmPayment = () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!recipientName.trim()) {
      toast.error("Enter the recipient's name");
      return;
    }
    const total = parseFloat(paymentAmount) + 2.99;
    if (!usdWallet || usdWallet.available_balance < total) {
      toast.error("Insufficient funds in USD wallet");
      return;
    }
    setPaymentStep(2);
  };

  // List View
  if (view === "list") {
    return (
      <div className="min-h-screen bg-background pb-24" data-testid="diaspora-bills-page">
        <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/bills")}
            className="text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading font-semibold text-xl">Diaspora Bills</h1>
            <p className="text-xs text-muted-foreground">Pay Jamaica bills from abroad</p>
          </div>
        </header>

        <main className="px-5 max-w-lg mx-auto">
          {/* Info Banner */}
          <div className="glass rounded-2xl p-4 mb-6 bg-gradient-to-r from-primary/10 to-primary/5">
            <div className="flex items-start gap-3">
              <Globe className="w-6 h-6 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-foreground text-sm">Pay bills for family in Jamaica</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Use your USD wallet to pay JPS, NWC, Flow, and Digicel bills for loved ones back home.
                </p>
              </div>
            </div>
          </div>

          {/* USD Wallet Balance */}
          {usdWallet && (
            <div className="glass rounded-2xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">USD Wallet Balance</p>
                    <p className="font-mono text-lg font-semibold">{formatUSD(usdWallet.available_balance)}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/exchange")}
                  className="btn-secondary text-xs"
                >
                  Add Funds
                </Button>
              </div>
            </div>
          )}

          {/* My Diaspora Billers */}
          {diasporaBillers.length > 0 && (
            <div className="mb-8">
              <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-4">
                My Diaspora Billers
              </p>
              <div className="space-y-3">
                {diasporaBillers.map((biller) => {
                  const logoConfig = providerConfig[biller.provider_id];
                  const iconConfig = providerIcons[biller.provider_id] || { 
                    icon: Receipt, 
                    color: "bg-gray-500"
                  };
                  const Icon = iconConfig.icon;
                  
                  return (
                    <button
                      key={biller.biller_id}
                      onClick={() => handleSelectBiller(biller)}
                      data-testid={`diaspora-biller-${biller.biller_id}`}
                      className="w-full glass rounded-2xl p-4 flex items-center gap-4 
                        hover:bg-white/10 transition-colors text-left"
                    >
                      {logoConfig ? (
                        <div className={`w-12 h-12 rounded-xl ${logoConfig.bgColor} border ${logoConfig.borderColor} flex items-center justify-center p-1.5 overflow-hidden`}>
                          <img 
                            src={logoConfig.logo} 
                            alt={logoConfig.name} 
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className={`w-12 h-12 rounded-xl ${iconConfig.color} flex items-center justify-center`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {biller.nickname || biller.provider_name.replace(" - Diaspora", "")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {biller.masked_account}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm font-semibold text-foreground">
                          {formatJMD(biller.outstanding_balance)}
                        </p>
                        <p className="text-xs text-green-400">
                          ≈ {formatUSD(biller.outstanding_balance / 157)}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add New Diaspora Biller */}
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-4">
              Add Diaspora Biller
            </p>
            <div className="grid grid-cols-2 gap-3">
              {providers?.map((provider) => {
                const logoConfig = providerConfig[provider.provider_id];
                const iconConfig = providerIcons[provider.provider_id] || { 
                  icon: Receipt, 
                  color: "bg-gray-500"
                };
                const Icon = iconConfig.icon;
                
                return (
                  <button
                    key={provider.provider_id}
                    onClick={() => { setSelectedProvider(provider); setView("add"); }}
                    data-testid={`diaspora-provider-${provider.provider_id}`}
                    className="glass rounded-2xl p-4 flex flex-col items-center gap-3 
                      hover:bg-white/10 transition-all hover:scale-105 active:scale-95"
                  >
                    {logoConfig ? (
                      <div className={`w-14 h-14 rounded-xl ${logoConfig.bgColor} border ${logoConfig.borderColor} flex items-center justify-center p-2 overflow-hidden`}>
                        <img 
                          src={logoConfig.logo} 
                          alt={logoConfig.name} 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className={`w-14 h-14 rounded-xl ${iconConfig.color} flex items-center justify-center`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                    )}
                    <span className="text-sm font-medium text-foreground text-center">
                      {logoConfig?.name || provider.name.replace(" - Diaspora", "").split(" ")[0]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Fee Info */}
          <div className="mt-8 glass rounded-2xl p-4">
            <p className="text-xs text-muted-foreground text-center">
              <strong>Flat $2.99 USD fee</strong> per diaspora bill payment.
              Exchange rate applied at time of payment.
            </p>
          </div>
        </main>

        <NavBar active="wallet" />
      </div>
    );
  }

  // Add Biller View
  if (view === "add" && selectedProvider) {
    const iconConfig = providerIcons[selectedProvider.provider_id] || { 
      icon: Receipt, 
      color: "bg-gray-500",
      gradient: "from-gray-500 to-gray-700"
    };
    const Icon = iconConfig.icon;

    return (
      <div className="min-h-screen bg-background" data-testid="add-diaspora-biller">
        <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setView("list"); setSelectedProvider(null); }}
            className="text-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-semibold text-xl">Add {selectedProvider.name.replace(" - Diaspora", "")}</h1>
        </header>

        <main className="px-5 max-w-lg mx-auto pb-8">
          <div className={`rounded-3xl p-6 mb-6 bg-gradient-to-br ${iconConfig.gradient}`}>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <Icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-heading font-semibold text-lg text-white">
                  {selectedProvider.name.replace(" - Diaspora", "")}
                </p>
                <p className="text-sm text-white/70">Jamaica</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">{selectedProvider.account_label}</label>
              <Input
                type="text"
                placeholder={selectedProvider.account_format}
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="input-glass mt-2"
                data-testid="diaspora-account-input"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Nickname (optional)</label>
              <Input
                type="text"
                placeholder="e.g., Mom's electricity"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="input-glass mt-2"
                data-testid="diaspora-nickname-input"
              />
            </div>

            <Button
              className="w-full btn-primary h-14 text-base mt-6"
              onClick={() => addBillerMutation.mutate()}
              disabled={!accountNumber || addBillerMutation.isPending}
              data-testid="add-diaspora-biller-btn"
            >
              {addBillerMutation.isPending ? "Adding..." : "Add Biller"}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Pay View
  if (view === "pay" && selectedBiller) {
    const iconConfig = providerIcons[selectedBiller.provider_id] || { 
      icon: Receipt, 
      color: "bg-gray-500",
      gradient: "from-gray-500 to-gray-700"
    };
    const Icon = iconConfig.icon;

    return (
      <div className="min-h-screen bg-background" data-testid="pay-diaspora-bill">
        <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setView("list"); setSelectedBiller(null); setPaymentStep(1); }}
            className="text-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-semibold text-xl">Pay Diaspora Bill</h1>
        </header>

        <main className="px-5 max-w-lg mx-auto pb-8">
          {/* Step 1: Enter Details */}
          {paymentStep === 1 && (
            <>
              <div className={`rounded-3xl p-6 mb-6 bg-gradient-to-br ${iconConfig.gradient}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-lg text-white">
                      {selectedBiller.nickname || selectedBiller.provider_name.replace(" - Diaspora", "")}
                    </p>
                    <p className="text-sm text-white/70">{selectedBiller.masked_account}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-white/20">
                  <p className="text-xs text-white/50">Outstanding Balance (JMD)</p>
                  <p className="font-mono text-2xl font-bold text-white">
                    {formatJMD(selectedBiller.outstanding_balance)}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Amount in USD */}
                <div>
                  <label className="text-sm text-muted-foreground">Amount to pay (USD)</label>
                  <div className="relative mt-2">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-muted-foreground">$</span>
                    <Input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="input-glass pl-10 text-2xl font-mono h-14"
                      data-testid="diaspora-amount-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    ≈ {formatJMD((parseFloat(paymentAmount) || 0) * 157)} JMD at current rate
                  </p>
                </div>

                {/* Recipient Name */}
                <div>
                  <label className="text-sm text-muted-foreground">Recipient name (whose bill is this?)</label>
                  <Input
                    type="text"
                    placeholder="e.g., Mary Smith"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    className="input-glass mt-2"
                    data-testid="recipient-name-input"
                  />
                </div>

                {/* Relationship */}
                <div>
                  <label className="text-sm text-muted-foreground">Relationship</label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {relationships.map((rel) => (
                      <button
                        key={rel.value}
                        onClick={() => setRelationship(rel.value)}
                        className={`p-3 rounded-xl border flex flex-col items-center gap-1 ${
                          relationship === rel.value
                            ? "border-primary bg-primary/10"
                            : "border-white/10 bg-white/5"
                        }`}
                      >
                        <span className="text-lg">{rel.icon}</span>
                        <span className="text-xs">{rel.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  className="w-full btn-primary h-14 text-base mt-4"
                  onClick={handleConfirmPayment}
                  data-testid="continue-diaspora-btn"
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Confirm */}
          {paymentStep === 2 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading font-semibold text-xl">Confirm Payment</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Paying bill for {recipientName}
                </p>
              </div>

              <div className="glass rounded-2xl p-4 space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Biller</span>
                  <span className="text-foreground">{selectedBiller.provider_name.replace(" - Diaspora", "")}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Account</span>
                  <span className="text-foreground font-mono">{selectedBiller.masked_account}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Recipient</span>
                  <span className="text-foreground">{recipientName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Relationship</span>
                  <span className="text-foreground capitalize">{relationship}</span>
                </div>
                <div className="border-t border-white/10 pt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount (USD)</span>
                    <span className="text-foreground font-mono">{formatUSD(parseFloat(paymentAmount))}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">≈ Amount (JMD)</span>
                    <span className="text-muted-foreground font-mono">{formatJMD(parseFloat(paymentAmount) * 157)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Fee (USD)</span>
                    <span className="text-foreground font-mono">$2.99</span>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-3 flex justify-between font-semibold">
                  <span className="text-foreground">Total (USD)</span>
                  <span className="text-primary font-mono">{formatUSD(parseFloat(paymentAmount) + 2.99)}</span>
                </div>
              </div>

              <div className="glass rounded-2xl p-4 flex items-center gap-3 mb-6">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
                <p className="text-xs text-muted-foreground">
                  Paying from USD Wallet (Balance: {formatUSD(usdWallet?.available_balance || 0)})
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  className="w-full btn-primary h-14 text-base"
                  onClick={() => payMutation.mutate()}
                  disabled={payMutation.isPending}
                  data-testid="confirm-diaspora-btn"
                >
                  {payMutation.isPending ? "Processing..." : "Confirm & Pay"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full btn-secondary h-12"
                  onClick={() => setPaymentStep(1)}
                >
                  Go Back
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Success */}
          {paymentStep === 3 && paymentResult && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                <Check className="w-10 h-10 text-primary" />
              </div>
              
              <h2 className="font-heading font-bold text-2xl mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                {recipientName}&apos;s bill has been paid
              </p>

              <div className="glass rounded-2xl p-4 text-left space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Confirmation</span>
                  <span className="text-primary font-mono">{paymentResult.confirmation_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount Paid (USD)</span>
                  <span className="text-foreground font-mono">{formatUSD(paymentResult.amount_usd)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount Paid (JMD)</span>
                  <span className="text-foreground font-mono">{formatJMD(paymentResult.amount_jmd)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Exchange Rate</span>
                  <span className="text-foreground font-mono">1 USD = {paymentResult.fx_rate} JMD</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Fee</span>
                  <span className="text-foreground font-mono">{formatUSD(paymentResult.fee_usd)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span className="text-foreground">
                    {new Date(paymentResult.created_at).toLocaleString()}
                  </span>
                </div>
              </div>

              <Button
                className="w-full btn-primary h-14 text-base"
                onClick={() => {
                  setView("list");
                  setSelectedBiller(null);
                  setPaymentStep(1);
                  setPaymentResult(null);
                }}
                data-testid="diaspora-done-btn"
              >
                Done
              </Button>
            </div>
          )}
        </main>
      </div>
    );
  }

  return null;
};

export default DiasporaBills;
